/**
 * The React Query layer over `pricing.ts` — and the place the scale rules are enforced once
 * instead of nine times.
 *
 * WHY THESE ARE HOOKS AND NOT NINE `useQuery` CALLS. Every surface that shows a price has to
 * get four things right, and every one of them is a bug that only appears at size:
 *
 *   1. ASK ONLY FOR WHAT IS ON SCREEN. The page's own rows, never the academy's catalogue.
 *      At 1000 courses the difference is a 2 KB request and a ~200 KB one.
 *   2. ONE REQUEST PER SCREENFUL, NEVER ONE PER CARD. The prohibition is measured twice in
 *      this repo — 41 requests became 1 for pathways (`paths.ts:56-62`), and the dashboard's
 *      per-card progress call became one bulk read (`catalogue.ts:671-687`).
 *   3. A STABLE QUERY KEY. The key is derived from the sorted, joined item keys inside a
 *      `useMemo`, so it is a scalar that changes only when the rows change — not on every
 *      render, and not when a session flag resolves from `undefined` to `true`.
 *   4. A STALE TIME. 5 minutes, the same as `useAcademyCourses.ts:31`, so moving between
 *      screens and refocusing the window does not re-ask for prices that have not moved.
 *
 * A surface that needs prices calls one of these and reads the seam through
 * `coursePriceView` / `pathwayPriceView`. It never touches a fetcher, a key or a query
 * option itself.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  adminPricesQueryKey,
  loadAdminPrices,
  loadPrices,
  loadPricingTenant,
  pricesQueryKey,
  PRICING_TENANT_KEY,
  type PricingSeam,
} from './pricing';

/** Long enough that a browse does not re-ask; short enough that a price edit lands. */
const PRICE_STALE_TIME = 5 * 60_000;

/**
 * Prices for a set of courses — the ones this screen is rendering, and no others.
 *
 * `courseIds` may be rebuilt on every render (it usually is, by a `.map()` over rows); the
 * key is derived from its CONTENTS, so a new array with the same ids is the same query.
 *
 * Returns `undefined` while it is in flight and while there is nothing to ask about, which
 * `coursePriceView` reads as `unknown` — so a card renders "Price unavailable" rather than
 * "Free" during the gap. That is the whole point of R6.0 and it is why this hook does not
 * pretend an empty answer is a loaded one.
 */
export function useCoursePrices(courseIds: readonly string[]): PricingSeam | undefined {
  const keys = useMemo(() => stable(courseIds), [courseIds.join('\u0000')]);

  const query = useQuery({
    queryKey: pricesQueryKey('courses', keys),
    queryFn: () => loadPrices({ courseIds: keys }),
    enabled: keys.length > 0,
    staleTime: PRICE_STALE_TIME,
    // `loadPrices` never rejects, so a retry could only ever repeat a request that already
    // answered. Failures arrive as a seam with `ok: false`.
    retry: false,
  });

  return query.data;
}

/** The same, for pathways. */
export function usePathwayPrices(pathwayKeys: readonly string[]): PricingSeam | undefined {
  const keys = useMemo(() => stable(pathwayKeys), [pathwayKeys.join('\u0000')]);

  const query = useQuery({
    queryKey: pricesQueryKey('pathways', keys),
    queryFn: () => loadPrices({ pathwayKeys: keys }),
    enabled: keys.length > 0,
    staleTime: PRICE_STALE_TIME,
    retry: false,
  });

  return query.data;
}

/**
 * One item, for a page landed on cold.
 *
 * A batch of one, so a course or pathway page never needs a list payload to be in cache to
 * show its own price — the property `PATH_SEAM_KEY`'s shared-cache trick gave up, and the
 * reason a detail page here costs one small request instead of the whole collection.
 *
 * It shares its key with a grid that happens to be showing the same single item, which is
 * the ordinary case of arriving from a one-card search result.
 */
export function useCoursePrice(courseId: string): PricingSeam | undefined {
  const keys = useMemo(() => (courseId ? [courseId] : []), [courseId]);
  return useCoursePrices(keys);
}

export function usePathwayPrice(pathwayKey: string): PricingSeam | undefined {
  const keys = useMemo(() => (pathwayKey ? [pathwayKey] : []), [pathwayKey]);
  return usePathwayPrices(keys);
}

/**
 * The tenant's gate and fee policy, with no item involved.
 *
 * For the authoring surfaces that must render the gate before anything is priced — a path
 * being created has no key yet, and a course with no price row still needs the disabled
 * control and the reason (R4.2). One tiny row under one key, shared by all of them.
 */
export function usePricingTenant(): PricingSeam | undefined {
  const query = useQuery({
    queryKey: PRICING_TENANT_KEY,
    queryFn: loadPricingTenant,
    staleTime: PRICE_STALE_TIME,
    retry: false,
  });
  return query.data;
}

/**
 * The ADMIN read for a set of courses: inactive rows included.
 *
 * Bounded exactly like the learner read. An admin course list at 1000 courses pages at 24
 * rows and asks about 24 prices — see `useAcademyCourses`, which is where the keys come
 * from.
 */
export function useAdminCoursePrices(courseIds: readonly string[]): PricingSeam | undefined {
  const keys = useMemo(() => stable(courseIds), [courseIds.join('\u0000')]);

  const query = useQuery({
    queryKey: adminPricesQueryKey('courses', keys),
    queryFn: () => loadAdminPrices({ courseIds: keys }),
    enabled: keys.length > 0,
    staleTime: PRICE_STALE_TIME,
    retry: false,
  });

  return query.data;
}

/** The admin read for one pathway — the authoring panel's own item. */
export function useAdminPathwayPrice(pathwayKey: string): PricingSeam | undefined {
  const keys = useMemo(() => (pathwayKey ? [pathwayKey] : []), [pathwayKey]);

  const query = useQuery({
    queryKey: adminPricesQueryKey('pathways', keys),
    queryFn: () => loadAdminPrices({ pathwayKeys: keys }),
    enabled: keys.length > 0,
    staleTime: PRICE_STALE_TIME,
    retry: false,
  });

  return query.data;
}

/**
 * Sorted and de-duplicated, so two screens showing the same rows in a different order share
 * one cache entry instead of fetching the same prices twice.
 *
 * Copied out of the caller's array on purpose: the caller owns that array and may mutate it
 * on the next render, and a query key that mutates under React Query is a cache entry that
 * can never be found again.
 */
function stable(keys: readonly string[]): string[] {
  return [...new Set(keys.filter(Boolean))].sort();
}
